import {envIsProd} from '../../config';
import {logHandledError} from '../errors/errorReporting';
import {type EventData} from './types';

/**
 * The guarded calls into `window.analytics`, lifted out of `./Analytics` so
 * `./shellAnalytics` has a sink to fall back to without calling `Analytics`
 * itself — which now resolves its sink through `getAnalytics()` and would
 * otherwise loop.
 *
 * The guards are the reason this layer exists at all, per `./README.md`:
 *
 *   - reading `window.analytics` too early gets the pre-init snippet stub, so
 *     the event is lost, and the caller should hear about it;
 *   - `window` may not exist, and `window?.analytics` does not help because it
 *     is the property access that throws, not the optional chain.
 *
 * Nothing outside `./Analytics` and `./shellAnalytics` should import this. New
 * events go through `makeTrackEvent`.
 */

export function trackViaGlobal(eventName: string, eventData: EventData): void {
  let trackCall;
  let initialized;
  try {
    trackCall = window.analytics?.track;
    initialized = (window.analytics as any)?.initialized;
  } catch {
    trackCall = null;
    initialized = false;
  }

  // only report in prod
  const captureMessage = envIsProd ? logHandledError : console.warn;

  // these represent conditions where expected behavior will fail
  // known conditions are that `window.analytics.track` exists but the service is
  // not initialized so it points to a non-functional track fn
  if (trackCall && initialized) {
    try {
      trackCall(eventName, eventData);
    } catch (err) {
      // this is probably an impossible edge case where Segment shows as initialized
      // but the tracking fn call is unvailable
      captureMessage(
        'Segment initialized, unknown error calling track function'
      );
    }
  } else if (trackCall && !initialized && envIsProd) {
    captureMessage('Segment not initialized, event is lost.');
  } else if (!trackCall && envIsProd) {
    captureMessage('Tracking event lost. Check Segment initialization.');
  }
}

export function pageViaGlobal(properties: object, options: object): void {
  let pageCall;
  let initialized;
  try {
    pageCall = window.analytics?.page;
    initialized = (window.analytics as any)?.initialized;
  } catch {
    pageCall = null;
    initialized = false;
  }

  if (pageCall && initialized) {
    try {
      pageCall(properties, options);
    } catch (err) {
      // this is probably an impossible edge case where Segment shows as initialized
      // but the page fn call is unavailable
      console.warn('Segment initialized, unknown error calling page function');
    }
  } else if (pageCall && !initialized && envIsProd) {
    console.warn('Segment not initialized, page event is lost.');
  } else if (!pageCall && envIsProd) {
    console.warn('Page event lost. Check Segment initialization.');
  }
}
